import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail, Phone, Send, Loader2, MapPin } from "lucide-react";
import { BRAND, CATEGORIES } from "@/data/site";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useI18n } from "@/context/I18nContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", company: "", country: "", email: "", phone: "", product: "", message: "" };

export default function Inquiry() {
  const { t } = useI18n();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("f_missing"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success(t("f_ok"));
      setForm(empty);
    } catch (err) {
      toast.error(t("f_err"));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-brand-border bg-brand-soft px-4 py-3 text-sm text-brand-fg placeholder:text-brand-muted focus:border-brand-gold focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-gold/40 transition-colors";

  const contacts = [
    { icon: MessageCircle, label: t("c_whatsapp"), value: BRAND.whatsapp, href: `https://wa.me/${BRAND.whatsappRaw}`, test: "contact-whatsapp", blank: true },
    { icon: Mail, label: t("c_email"), value: BRAND.email, href: `mailto:${BRAND.email}`, test: "contact-email" },
    { icon: Phone, label: t("c_call"), value: BRAND.phone, href: `tel:${BRAND.phone.replace(/\s/g, "")}`, test: "contact-phone" },
  ];

  return (
    <section id="inquiry" data-testid="inquiry-section" className="relative z-10 bg-brand-soft border-t border-brand-border">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-28 lg:py-40">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          <div>
            <Reveal><Eyebrow>{t("inq_eyebrow")}</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand-fg leading-[1.02]">
                {t("inq_title1")} <span className="text-brand-gold italic">{t("inq_title2")}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}><p className="mt-6 max-w-md text-brand-fg/70 leading-relaxed">{t("inq_intro")}</p></Reveal>

            <div className="mt-10 space-y-3">
              {contacts.map((c) => (
                <Reveal key={c.label} delay={0.12}>
                  <a href={c.href} target={c.blank ? "_blank" : undefined} rel="noreferrer" data-testid={c.test} className="flex items-center gap-4 rounded-xl border border-brand-border bg-brand-card px-5 py-4 hover:border-brand-gold/60 transition-colors group shadow-sm">
                    <span className="grid place-items-center h-11 w-11 rounded-full bg-brand-goldSoft text-brand-gold group-hover:bg-brand-gold group-hover:text-white transition-colors">
                      <c.icon size={18} />
                    </span>
                    <span>
                      <span className="block text-[11px] uppercase tracking-[0.2em] text-brand-muted">{c.label}</span>
                      <span className="block text-brand-fg group-hover:text-brand-gold transition-colors">{c.value}</span>
                    </span>
                  </a>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.16}>
              <div className="mt-4 overflow-hidden rounded-xl border border-brand-border shadow-sm" data-testid="map-embed">
                <iframe title="Sunrise Mart location — Palamaner" src="https://maps.google.com/maps?q=13.2055278,78.7343611&z=15&output=embed" className="w-full h-64 border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                <a href={BRAND.mapUrl} target="_blank" rel="noreferrer" data-testid="map-open-link" className="flex items-center justify-between gap-2 bg-brand-card px-5 py-3.5 text-sm transition-colors hover:bg-brand-soft">
                  <span className="flex items-center gap-2 text-brand-fg"><MapPin size={15} className="text-brand-gold" /> Sunrise Mart · Palamaner</span>
                  <span className="text-brand-gold">{t("map_open")}</span>
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <form onSubmit={submit} data-testid="inquiry-form" className="rounded-2xl border border-brand-border bg-brand-card p-7 sm:p-10 shadow-sm">
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="input-name" className={inputCls} placeholder={t("f_name")} value={form.name} onChange={update("name")} />
                <input data-testid="input-company" className={inputCls} placeholder={t("f_company")} value={form.company} onChange={update("company")} />
                <input data-testid="input-email" type="email" className={inputCls} placeholder={t("f_email")} value={form.email} onChange={update("email")} />
                <input data-testid="input-phone" className={inputCls} placeholder={t("f_phone")} value={form.phone} onChange={update("phone")} />
                <input data-testid="input-country" className={inputCls} placeholder={t("f_country")} value={form.country} onChange={update("country")} />
                <select data-testid="input-product" className={inputCls} value={form.product} onChange={update("product")}>
                  <option value="">{t("f_product")}</option>
                  {CATEGORIES.map((c) => (<option key={c.id} value={c.name}>{c.name}</option>))}
                </select>
              </div>
              <textarea data-testid="input-message" className={`${inputCls} mt-4 min-h-[120px] resize-none`} placeholder={t("f_message")} value={form.message} onChange={update("message")} />
              <button type="submit" disabled={loading} data-testid="inquiry-submit" className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-brand-gold px-8 py-3.5 text-sm font-semibold text-white hover:bg-brand-gold/90 transition-colors disabled:opacity-60">
                {loading ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                {loading ? t("f_sending") : t("f_submit")}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
